import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { campaignPerformanceQuery } from '@/hooks/analytics/analytics-queries';
import { useTenant } from '@/hooks/use-tenant';

/** Days of campaigns behind the store average (Assumptions, CAMP-03). */
const WINDOW_DAYS = 90;

/** What a campaign is compared with on its details (CAMP-03). */
export interface StoreCampaignAverages {
  /** Paid orders ÷ notifications sent of the window; `null` without sends. */
  conversion: number | null;
  /** `averageCtr` (ratio). */
  ctr: number;
  /** `averageRevenue`. */
  revenue: number;
  /** Campaigns sent in the window (the comparison needs 2+). */
  campaignsCount: number;
}

/**
 * Averages of the campaigns sent from today − 90 days 00:00 until now (the
 * campaign itself included), from `/metrics/campaigns`. `null` while
 * disabled, loading or failed: no comparison labels then.
 */
export function useStoreCampaignAverages({
  enabled,
}: {
  enabled: boolean;
}): StoreCampaignAverages | null {
  const tenant = useTenant();
  const [range] = useState(() => {
    const now = DateTime.now();
    return {
      from: now.minus({ days: WINDOW_DAYS }).startOf('day').toISO() ?? '',
      to: now.toISO() ?? '',
    };
  });

  const query = useQuery({
    ...campaignPerformanceQuery(tenant.slug, range),
    enabled,
  });

  const data = query.data;
  if (!enabled || !data) return null;
  return {
    conversion:
      data.totalNotificationsSent === 0
        ? null
        : data.totalOrderPaidCount / data.totalNotificationsSent,
    ctr: data.averageCtr,
    revenue: data.averageRevenue,
    campaignsCount: data.sentCampaignsCount,
  };
}
